import * as Config from '../../config_module';
import '@netexknowledge/smart-socket-connection';
import { emmit } from '../../listeners/event/eventEmmitter';
import * as EventNames from '../../listeners/event/eventNames';
import * as RouteUtils from '../../listeners/route/routeUtils';
import { MESSAGES_SOCKET, RABBIT_CONNECTION } from '../../const';
import {
  forceTryToConnect,
  setConnectionStatusRabbitState,
  setConnectionAttempts
} from '../../actions/connectionActions';
import { getLogger } from '../logger';
import * as jwtService from '../../services/jwt/jwtService';
import { getTokenFromStorage } from '../../shared';

const debug = getLogger('service:socketConnection:socketSubscriberService');

let dispatch;
let getState;

const getUserInformation = () => {
  let state = getState();
  let userInfo = {};
  let reduxStoreUser = state.account.user;
  userInfo.role = reduxStoreUser.role;
  userInfo.userId = reduxStoreUser._id;
  userInfo.platform = 'classroom';
  userInfo.tenantId = jwtService.getSchoolId(getTokenFromStorage());
  userInfo.ltiContextId = state.classrooms.selectedClassroom.classroom.id;
  return userInfo;
};

const getSocketConnection = () => {
  return window.SocketConnection;
};

const manageConnection = async () => {
  Config.RABBIT_CONFIG.userInfo = getUserInformation();
  let sc = getSocketConnection().getInstance();

  if (sc.isConnected()) return;

  sc.setDebugLevel("ALL")

  if (Config.RABBIT_CONFIG.connection.authentication.pass === "token") {
    Config.RABBIT_CONFIG.connection.authentication.pass = getTokenFromStorage();
  }

  sc.onChangeStatus = function(status) {
    switch (status) {
      case getSocketConnection().CONNECTION_STATUS.RECONNECTING:
        dispatch(setConnectionStatusRabbitState(RABBIT_CONNECTION.WAIT));
        dispatch(forceTryToConnect());
        break;

      case getSocketConnection().CONNECTION_STATUS.CONNECTED:
        dispatch(setConnectionStatusRabbitState(RABBIT_CONNECTION.OK));
        break;

      case getSocketConnection().CONNECTION_STATUS.CONNECTION_ERROR:
        dispatch(setConnectionStatusRabbitState(RABBIT_CONNECTION.KO));
        break;

      default:
        break;
    }
  };

  await sc.init(Config.RABBIT_CONFIG);

  sc.notifications.onStudentLeft = function(message) {
    manageUserConnection(message, false);
  };

  sc.notifications.onStudentJoin = function(message) {
    manageUserConnection(message, true);
  };

  function dispatchMessageByType(messageType, messageData) {
    let user = getState().account.user;
    let bookId = RouteUtils.getBookId();
    switch (messageType) {
      case MESSAGES_SOCKET.DISCUSSION_UPDATED:
        emmit(EventNames.DISCUSSIONS.UPDATED, messageData);
        break;
      case MESSAGES_SOCKET.DISCUSSION_CREATED:
        emmit(EventNames.DISCUSSIONS.CREATED, messageData);
        break;
      case MESSAGES_SOCKET.DISCUSSION_DELETED:
        emmit(EventNames.DISCUSSIONS.DELETED, messageData);
        break;
      case MESSAGES_SOCKET.MESSAGE_CREATED:
        emmit(EventNames.DISCUSSIONS.MESSAGES.CREATED, messageData);
        break;
      case MESSAGES_SOCKET.MESSAGE_UPDATED:
        emmit(EventNames.DISCUSSIONS.MESSAGES.UPDATED, messageData);
        break;
      case MESSAGES_SOCKET.GROUP_UPDATE:
        if (user.email === messageData.userId) {
          emmit(EventNames.USER_GROUP.name, {
            classId: messageData.sectionId,
            bookId: bookId,
            action: messageData.action
          });
        }
        break;
      case MESSAGES_SOCKET.STUDENT_UPDATE:
        if (user._id === messageData.userId) {
          emmit(EventNames.STUDENT_BOOK_UPDATE, {});
        }
        break;
      case MESSAGES_SOCKET.ACTIVITY_UPDATE:
        emmit(EventNames.BOOK.name, {
          classId: messageData.sectionId,
          bookId: bookId,
          unitActivityId: messageData.unitUri,
          action: messageData.action
        });
        break;
      case MESSAGES_SOCKET.CLASSROOM_UPDATE:
        emmit(EventNames.CLASSROOM.name, {
          classId: messageData.sectionId,
          action: messageData.action
        });
        break;
      case MESSAGES_SOCKET.TASK_UPDATE:
        emmit(EventNames.TASK.name, {
          classId: messageData.sectionId,
          action: messageData.action,
          taskId: messageData.assignmentId,
          studentId: messageData.studentId
        });
        break;
      case MESSAGES_SOCKET.STUDENT_ENROLLMENT:
      case MESSAGES_SOCKET.STUDENT_UNENROLLMENT:
        emmit(EventNames.STUDENT.name, {
          action: messageType,
          userId: messageData.user.userId,
          role: messageData.role
        });
        break;
      case MESSAGES_SOCKET.TEACHER_ENROLLMENT:
      case MESSAGES_SOCKET.TEACHER_UNENROLLMENT:
        emmit(EventNames.TEACHER.name, {
          action: messageType,
          userId: messageData.user.userId,
          role: messageData.role
        });
        break;
      case MESSAGES_SOCKET.VIDEOCALL_STARTED:
        emmit(EventNames.VIDEOCALL.name, {
          classId: messageData.sectionId,
          videocallUrl: messageData.videocallUrl,
          action: messageData.action
        });
        break;
      case MESSAGES_SOCKET.GOOGLE_COURSEWORK_FINISHED:
        emmit(EventNames.GOOGLE.name, {
          action: messageType,
          googleResponseStatus: messageData.googleResponseStatus,
          sectionId: messageData.sectionId,
          userId: messageData.userId
        });
        break;
      default:
        break;
    }
  }

  sc.notifications.onStudentNotification = function(message) {
    try {
      manageUserNotification(message);
    } catch (e) {
      debug.error('error when receiving a student notification', e);
    }
  };

  sc.notifications.onTeacherNotification = function(message) {
    try {
      manageUserNotification(message);
    } catch (e) {
      debug.error('error when receiving a teacher notification', e);
    }
  };

  sc.notifications.onClassroomNotification = function(message) {
    const body = parseMessageBody(message);
    const type = body.type ? body.type : body.message;
    if (body.data || body.resource) {
      const data = body.data ? body.data : JSON.parse(body.resource);
      dispatchMessageByType(type, data);
    }
  };

  function manageUserConnection(message, connectionStatus) {
    let classroom = getState().classrooms.selectedClassroom.classroom;

    let body = {};
    try {
      body = JSON.parse(message.body);
    } catch (err) {
      body = { error: err };
    }

    if (body.userId && body.sectionId && body.sectionId !== classroom.id) return;
    let data = {
      userId: body.userId,
      connectionStatus,
      classId: body.sectionId
    };

    emmit(EventNames.STUDENT_PRESENCE, { participant: data, classroom: classroom });
  }

  function manageUserNotification(message) {
    const classroom = getState().classrooms.selectedClassroom.classroom;
    const body = parseMessageBody(message);

    const messageType = body.message;
    const messageData = body.data;
    if (messageData.sectionId && messageData.sectionId !== classroom.id) return;

    if (messageType) {
      dispatchMessageByType(messageType, messageData);
    }
  }

  sc.onReconnectAttempt = function(attempt) {
    dispatch(setConnectionAttempts(attempt));
  };

  function parseMessageBody(message) {
    let body = {};

    if (typeof message.body === 'object') {
      body = message.body;
    } else if (typeof message.body === 'string') {
      try {
        body = JSON.parse(message.body);
      } catch (err) {
        body = { error: 'Error parsing JSON' };
      }
    }

    return body;
  }
};

export const startConnection = store => {
  if (!dispatch && !getState) {
    dispatch = store.dispatch;
    getState = store.getState;
  }

  if (
    getState().account.configuration.enableClassroomWebSocket &&
    getState().account.user._id &&
    getState().classrooms.selectedClassroom.classroom.id
  ) {
    manageConnection();
  }
};

export const init = () => {
  Config.RABBIT_CONFIG.userInfo = getUserInformation();
  return window.SocketConnection.getInstance().init(Config.RABBIT_CONFIG);
};

export const destroyConnection = () => {
  let sc = window.SocketConnection.getInstance();
  // Evaluate if call the sendLeft inside disconnect
  if (sc.isConnected()) {
    try {
      sc.sendLeft().catch(error => {
        debug.error('error sending left %s', error);
      });
    } catch (e) {
      debug.error('error sending left %s', e);
    }
  }

  try {
    return sc.disconnect().catch(error => {
      debug.error('error during disconnection %s', error);
    });
  } catch (e) {
    debug.error('error during disconnection %s', e);
  }
};
